import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCategories } from "../context/StoreDataContext";
import { resolveImageUrl } from "../lib/images";
import ProductCard from "../components/ProductCard";
import HeroCarousel from "../components/HeroCarousel";
import ShippingSection from "../components/ShippingSection";
import PromotionsSection from "../components/PromotionsSection";
import { ArrowRight, Sparkles, Heart, Gift } from "lucide-react";

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const { categories } = useCategories();

    useEffect(() => {
        api.get("/products", { params: { featured: true } })
            .then((r) => setFeatured(r.data.slice(0, 4)))
            .catch(() => setFeatured([]));
    }, []);

    return (
        <div data-testid="home-page">
            <HeroCarousel />

            <PromotionsSection />

            {/* CATEGORIES BENTO */}
            <section className="px-6 md:px-12 lg:px-20 py-16 lg:py-24" data-testid="categories-section">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
                    <div>
                        <p className="overline">Explora por categoría</p>
                        <h2 className="font-serif text-4xl sm:text-5xl text-charcoal mt-3">
                            Encuentra <span className="italic">tu pieza.</span>
                        </h2>
                    </div>
                    <Link
                        to="/catalogo"
                        className="text-sm text-gold hover:text-gold-dark inline-flex items-center gap-2 tracking-wider uppercase"
                    >
                        Ver todo <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {categories.slice(0, 8).map((c) => (
                        <Link
                            key={c.id}
                            to={`/catalogo?cat=${c.slug}`}
                            className="group relative aspect-[4/5] rounded-2xl overflow-hidden block bg-blush"
                            data-testid={`category-card-${c.slug}`}
                        >
                            {c.image_url ? (
                                <img
                                    src={resolveImageUrl(c.image_url)}
                                    alt={c.name}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                            ) : null}
                            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-charcoal/10 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-5">
                                <h3 className="font-serif text-2xl text-white">{c.name}</h3>
                                <p className="text-xs tracking-[0.2em] uppercase text-white/80 mt-1 inline-flex items-center gap-2">
                                    Comprar <ArrowRight strokeWidth={1.5} className="w-3 h-3" />
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* FEATURED PRODUCTS */}
            {featured.length > 0 && (
                <section className="px-6 md:px-12 lg:px-20 py-16 lg:py-24" data-testid="featured-section">
                    <div className="text-center mb-12">
                        <p className="overline">Selección de la temporada</p>
                        <h2 className="font-serif text-4xl sm:text-5xl text-charcoal mt-3">
                            Favoritas <span className="italic">de la casa.</span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="featured-grid">
                        {featured.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                </section>
            )}

            <ShippingSection />

            {/* VALUES */}
            <section className="px-6 md:px-12 lg:px-20 py-16 lg:py-24">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-5xl mx-auto">
                    {[
                        { icon: Heart, t: "Hecho con amor", d: "Cada pieza es ensamblada a mano con dedicación." },
                        { icon: Sparkles, t: "Materiales nobles", d: "Chapas resistentes, perlas y cristales cuidadosamente seleccionados." },
                        { icon: Gift, t: "Empaque especial", d: "Tu pedido llega listo para regalar, en bolsa de tela rosa." },
                    ].map((v, i) => (
                        <div key={i} className="text-center">
                            <v.icon strokeWidth={1.5} className="w-7 h-7 text-gold mx-auto" />
                            <h3 className="font-serif text-2xl text-charcoal mt-4">{v.t}</h3>
                            <p className="text-sm text-taupe mt-2 leading-relaxed">{v.d}</p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
